/**
 * Client-side merge-field preview: substitute `{{ns.field}}` tokens in an email
 * subject/body with concrete values so the editor can show the email the way a
 * customer receives it — business info from the tenant's Company settings, and
 * representative sample values for the per-document fields. This mirrors the
 * server's `resolveMergeFields` closely enough for a faithful preview; the real
 * send still renders server-side.
 */
import type { CSSProperties } from 'react';
import {
  renderEmailShell,
  resolveEmailBranding,
  styleCtaButtons,
  type EmailBranding,
  type EmailBrandingOverride,
} from '@notifi/shared-types/email-layout';
import type { CompanySettings } from '@/types/settings';
import { sampleContextFor } from '@notifi/shared-types/email-kinds';
import type { EmailTemplateKind } from '@/types/emailTemplate';

/**
 * The email's base font + text style. Email clients don't load the app's UI font
 * (Inter), so the editor, the preview, and the sent message all render in this
 * email-safe stack instead — so what the author sees matches what is delivered.
 * Keep in sync with the backend render wrapper.
 */
export const EMAIL_BASE_FONT =
  'Inter, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
export const EMAIL_BASE_STYLE: CSSProperties = {
  fontFamily: EMAIL_BASE_FONT,
  color: '#111827',
  fontSize: '15px',
  lineHeight: 1.6,
};

/**
 * Stand-in shown for the `{{business.logo}}` image while editing and in the
 * preview when the tenant has no logo yet — a dashed "Your logo" box, so the
 * author sees an intentional placeholder instead of a broken-image icon. The
 * real send substitutes the tenant's logo URL.
 */
export const EMAIL_LOGO_PLACEHOLDER =
  'data:image/svg+xml,' +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='160' height='56'>" +
      "<rect x='1' y='1' width='158' height='54' rx='8' fill='#f3f4f6' stroke='#d1d5db' stroke-dasharray='4 4'/>" +
      "<text x='80' y='32' font-family='sans-serif' font-size='13' fill='#9ca3af' text-anchor='middle'>Your logo</text>" +
      '</svg>',
  );

// Mirror the backend's formatTenantPostalAddress (shared/email/tenant-email-company)
// so the previewed `{{business.address}}` reads exactly like the sent one: parts
// joined with ", ", but state + zip grouped with a single space ("IL 62704").
function formatAddress(a?: CompanySettings['address']): string {
  if (!a) return '';
  const parts = [a.street, a.city, [a.state, a.zip].filter(Boolean).join(' ')].filter(Boolean);
  return parts.join(', ');
}

/**
 * Build the token→value map for a preview of `kind`, from real company data. The
 * `company` carries the effective business identity — in the Business-Unit editor
 * it is the tenant company with the BU's overrides applied (including its own
 * `legalName`), so `business.*` mirrors what the send resolves via
 * `resolveBusinessMergeFields`. Tenant company settings have no distinct legal
 * name, so `legalName` falls back to the display name.
 */
export function buildPreviewContext(
  kind: EmailTemplateKind,
  company?: CompanySettings & { legalName?: string | null },
): Record<string, string> {
  const business: Record<string, string> = {
    'business.displayName': company?.name ?? 'Your Company',
    'business.legalName': company?.legalName || company?.name || 'Your Company, LLC',
    'business.address': formatAddress(company?.address),
    'business.phone': company?.phone ?? '(555) 123-4567',
    'business.email': company?.email ?? 'hello@yourcompany.com',
    'business.website': company?.website ?? 'yourcompany.com',
    'business.taxId': company?.taxRegistrationNumber ?? '',
    'business.tagline': company?.tagline ?? '',
    'business.license': company?.contractorLicense ?? '',
    'business.logo': company?.logoUrl || EMAIL_LOGO_PLACEHOLDER,
  };
  // Sample values for the kind's own tokens come from the shared registry —
  // the same source the palette lists, so no offered token previews as braces.
  return { ...business, ...sampleContextFor(kind) };
}

/**
 * Replace `{{ token }}` occurrences using `ctx`. Unknown tokens are left as a
 * subtle placeholder so the author notices a typo rather than shipping braces.
 */
export function renderPreview(html: string, ctx: Record<string, string>): string {
  return html.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, token: string) => {
    if (Object.prototype.hasOwnProperty.call(ctx, token)) return ctx[token];
    return `<span style="color:#b91c1c">${match}</span>`;
  });
}

/**
 * The brand tokens a preview renders with. The tenant company carries no
 * colours/fonts of its own today (documents brand from the design), so the
 * preview brands from the defaults, the BU's overrides when the caller has
 * them, and the design's own override — the same precedence the send resolves.
 */
export function previewBranding(
  company?: { logoUrl?: string | null } | null,
  brand?: EmailBrandingOverride | null,
  override?: EmailBrandingOverride | null,
): EmailBranding {
  return resolveEmailBranding(
    {
      ...(brand ?? {}),
      logoUrl: company?.logoUrl ?? null,
      // The platform wordmark under the card, served by this app (the send
      // points at the same file on the public origin).
      platformLogoUrl:
        typeof window !== 'undefined' && window.location?.origin
          ? `${window.location.origin}/brand/notifi-email-logo.png`
          : null,
    },
    override,
  );
}

/**
 * Render the full branded email preview HTML for a LEGACY message body: the
 * shared shell (card + header + footer) around the body, with merge fields
 * resolved from `ctx` and the CTA link styled as a button. Mirrors the backend
 * render exactly — it is the same renderer.
 */
export function renderEmailPreviewHtml(
  bodyHtml: string,
  ctx: Record<string, string>,
  branding?: EmailBrandingOverride | null,
): string {
  const body = renderPreview(styleCtaButtons(bodyHtml, branding), ctx);
  return renderPreview(renderEmailShell('\u0000BODY\u0000', branding), ctx).replace(
    '\u0000BODY\u0000',
    () => body,
  );
}
